import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SERVICES } from "../data/services";
import { Seo, breadcrumbSchema } from "../components/Seo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { TrustStrip } from "../components/TrustStrip";
import { CtaBlock } from "../components/CtaBlock";
import { Reveal, Eyebrow } from "../components/Reveal";
import { Img } from "../components/Img";

export default function ServicesHub() {
  return (
    <div data-testid="services-hub-page">
      <Seo
        title="Collision Repair Services in Carrollton, TX | Westway"
        description="All 15 certified auto body services in Carrollton, TX: collision repair, frame machine, PDR, hail, paint booth, ADAS & more. Free estimates: (214) 888-2886."
        path="/services"
        schemas={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Services", path: "/services" }])]}
      />

      <section className="relative pt-32 lg:pt-40 pb-16 overflow-hidden">
        <div className="absolute inset-0 surface-weave opacity-50" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Services", path: "/services" }]} />
          <Reveal>
            <Eyebrow>Full Capability</Eyebrow>
            <h1 className="mt-5 font-display text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.06] text-balance max-w-4xl">
              Collision &amp; Auto Body Services in Carrollton, TX
            </h1>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed">
              Fifteen certified services under one roof — from computerized frame straightening to
              paintless dent repair — all performed to OEM procedure and backed by a written
              lifetime warranty. Every service below includes free estimates and direct insurance
              claim handling.
            </p>
          </Reveal>
        </div>
      </section>

      <TrustStrip />

      <section className="pt-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="clip-frame overflow-hidden rounded-sm border border-white/10">
              <Img name="westway-auto-repair-bay" alt="Inside the repair bay at Westway Collision Center in Carrollton, TX" eager className="h-56 sm:h-72 w-full object-cover" sizes="100vw" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={(i % 3) * 0.06}>
              <Link
                to={`/services/${s.slug}`}
                data-testid={`hub-service-${s.slug}`}
                className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-white/[0.08] bg-surface transition-all duration-500 hover:border-accent/30 hover:-translate-y-1.5"
              >
                <div className="relative h-44 overflow-hidden">
                  <Img name={s.img} alt={s.alt} className="block mx-auto h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.06]" sizes="(min-width: 1024px) 33vw, 100vw" />
                  <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/30 to-transparent" aria-hidden="true" />
                  <span className="absolute top-4 left-4 rounded-full border border-accent/30 bg-ink/70 backdrop-blur-md px-3 py-1.5 font-mono text-[9px] tracking-[0.2em] uppercase text-accent">
                    {s.badge}
                  </span>
                </div>
                <div className="flex flex-1 items-center justify-between gap-3 p-6">
                  <h2 className="font-display text-lg font-semibold text-slate-100 group-hover:text-accent transition-colors">{s.name}</h2>
                  <ArrowRight size={17} className="shrink-0 text-mute group-hover:text-accent group-hover:translate-x-1 transition-all" aria-hidden="true" />
                </div>
              </Link>
            </Reveal>
          ))}
          <Reveal delay={0.1}>
            <Link
              to="/dme-tuning-dallas"
              data-testid="hub-dme-link"
              className="group relative flex h-full min-h-64 flex-col justify-between overflow-hidden rounded-2xl border border-accent/30 bg-gradient-to-br from-accent/[0.12] via-surface to-surface p-6 transition-all duration-500 hover:border-accent/60 hover:-translate-y-1.5"
            >
              <span className="rounded-full border border-accent/40 bg-accent/10 px-3 py-1.5 font-mono text-[9px] tracking-[0.2em] uppercase text-accent w-fit">
                Flagship Performance
              </span>
              <div>
                <h2 className="font-display text-2xl font-bold text-white">DME Tuning &amp; Repair</h2>
                <p className="mt-2 text-sm text-slate-400">Performance ECU tuning, DME diagnostics and exotic collision repair.</p>
                <span className="mt-4 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-accent">
                  Explore <ArrowRight size={13} />
                </span>
              </div>
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <CtaBlock />
        </div>
      </section>
    </div>
  );
}
